import { useEffect } from "react";
import { useSiteConfig } from "../context/SiteConfigContext";

/**
 * Per-page SEO override. Sets <title>, <meta description>, and og:* tags
 * based on `config.seo_pages[pageKey]` with fallbacks to the supplied
 * defaults or the global site SEO values.
 */
const setMeta = (selector, attr, value) => {
    if (!value) return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        const [, a, name] = selector.match(/meta\[(\w+)="([^"]+)"\]/) || [];
        if (!a || !name) return;
        el.setAttribute(a, name);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
};

export default function SeoHead({ pageKey, title: defaultTitle, description: defaultDescription, image: defaultImage }) {
    const { config } = useSiteConfig();

    useEffect(() => {
        if (!config) return;
        const pageOverride = config.seo_pages?.[pageKey] || {};
        const title =
            pageOverride.title?.trim() ||
            defaultTitle ||
            config.seo_title ||
            config.site_name;
        const description =
            pageOverride.description?.trim() ||
            defaultDescription ||
            config.seo_description ||
            "";
        const image = pageOverride.og_image?.trim() || defaultImage || config.home_hero_image || "";

        if (title) document.title = title;
        setMeta('meta[name="description"]', "content", description);
        setMeta('meta[property="og:title"]', "content", title);
        setMeta('meta[property="og:description"]', "content", description);
        if (image) setMeta('meta[property="og:image"]', "content", image);
        setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
        setMeta('meta[name="twitter:title"]', "content", title);
        setMeta('meta[name="twitter:description"]', "content", description);
        if (image) setMeta('meta[name="twitter:image"]', "content", image);
    }, [config, pageKey, defaultTitle, defaultDescription, defaultImage]);

    return null;
}
